import type {
	FoxItAnnot,
	FoxItAnnotScreen,
	FoxItField,
	FoxItPDFForm,
} from '@/FoxItTypes'
import { FoxItHandState, FoxItScreen } from '@/FoxItTypes'
import Plan from '@/classes/Plan'
import {
	getNormalizedSignatureFieldName,
	saveUnsignedSignaturePlacementInfo,
	setSignatureDateField,
	setSignatureRelatedTextField,
	isFuneralDirectorSignature,
} from '@/components/documents/Foxit/FoxItHelpers'
import { useFoxitFormFilling } from '@/components/documents/Foxit/composables/FoxitFormFilling'
import { useFoxitInstance } from '@/components/documents/Foxit/composables/FoxitInstance'
import { useFoxitUI } from '@/components/documents/Foxit/composables/FoxitUI'
import { setAdditionalRequiredFields } from '@/components/documents/Foxit/composables/RequiredFieldsProvider'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { persistFoxitSignatures } from '@/services/foxitSignatures'
import { useDocumentStore } from '@/stores/document'
import { useSessionStore } from '@/stores/session'
import type { PageStatusType } from '@/stores/sessionDocuments'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { MissingSignaturesBySigner, Signer, StoreDocument } from '@/types'
import { PDFViewCtrl } from '@foxitsoftware/foxit-pdf-sdk-for-web-library'
import { captureException } from '@sentry/vue'
import { DateTime } from 'luxon'
import { ref, toRaw } from 'vue'

const shouldNotRender = ref(false)
const debugEvents = ref(false)
const activeSignature = ref<string | null>(null)
const confirmModalOpen = ref(false)
const { getPdfui, getPdfViewer } = useFoxitInstance()

let pageStateQueue: Promise<void> = Promise.resolve()
let pageStateGeneration: number = 0

export const useFoxitManager = () => {
	const documentStore = useDocumentStore()
	const sessionDocumentsStore = useSessionDocumentsStore()
	const sessionStore = useSessionStore()
	const { setFieldValues } = useFoxitFormFilling()
	const {
		revertAllSignedFieldsToButtons,
		turnAllSignaturesToPushButtons,
		appendUuidToSignatureDateFields,
		clearActiveSignature,
		showSignatureDropdown,
		showSignatureDialog,
		revertSignedAnnotationToButton,
		addCursiveFontToSignatureDialog,
		setFilledFieldsToTransparent,
	} = useFoxitUI()

	const pageStateListener = (
		newState: {
			status: PageStatusType
			meta?: Record<any, any>
		},
		oldState: {
			status: PageStatusType
			meta?: Record<any, any>
		} | null = null
	) => {
		const generation = ++pageStateGeneration

		pageStateQueue = pageStateQueue
			.catch(() => {
				console.error('Error in pageStateQueue')
			})
			.then(async () => {
				if (generation !== pageStateGeneration) {
					return // a newer pageState already queued; skip this one
				}

				await handlePageStateChange(newState, oldState)
			})

		return pageStateQueue
	}

	const handlePageStateChange = async (
		newState: {
			status: PageStatusType
			meta?: Record<any, any>
		},
		oldState: {
			status: PageStatusType
			meta?: Record<any, any>
		} | null = null
	) => {
		const shouldSaveBeforeTransition =
			newState.status === 'switch' ||
			(oldState?.status === 'ready' &&
				newState.status !== 'pending' &&
				oldState?.meta?.planHolderID)

		if (shouldSaveBeforeTransition) {
			await saveCurrentDocument()
		}

		switch (newState.status) {
			case 'switch':
				await openPDF(newState.meta!.planHolderID, newState.meta!.document)
				break
			case 'merging':
				await prepareMergedDocuments()
				break
		}
	}

	// Saves the current document's blob in the store before opening the new document
	const saveCurrentDocument = async () => {
		const documentToSave = sessionDocumentsStore.currentDocument
		if (!documentToSave) {
			return
		}

		const pdfViewer = getPdfViewer()
		if (!pdfViewer) {
			captureException(
				new Error('Error saving current document. PDF viewer not available.'),
				{
					extra: {
						message: 'Error saving current document. PDF viewer not available.',
					},
				}
			)
			return
		}

		const viewerDoc = pdfViewer.getCurrentPDFDoc()

		if (viewerDoc) {
			const viewDocFile = await viewerDoc!.getFile()
			const arrayBuffer = await viewDocFile.arrayBuffer()

			try {
				await documentStore.updatePlanHolderDocument(
					arrayBuffer,
					documentToSave
				)

				// Only clear if current is still the doc we just saved
				if (sessionDocumentsStore.currentDocument?.id === documentToSave.id) {
					sessionDocumentsStore.clearCurrentDocument()
				}
			} catch (error) {
				captureException(error, {
					extra: {
						message: "Error updating PlanHolder's documents",
					},
				})
			}
		}
	}

	/**
	 * Merges together all the documents except those with payment info so we
	 * can send it to Everplans.
	 * @param planHolder
	 */
	const mergeEverplansDocsForPlanholder = async (planHolder: Plan) => {
		const completedDocuments = documentStore
			.getPlanHolderDocuments(planHolder)
			.filter((document) =>
				planHolder.completedSteps.includes(document.meta.type)
			)

		// get list of fields we should redact in each document
		const redactedDocuments = await Promise.all(
			completedDocuments.map(redactDocument)
		)

		// feed it to mergeDocs
		const mergedDocWithoutPaymentInfo = await mergeDocs(redactedDocuments)

		// store the flattenedArray as everplans CompletePDF in ref and in local forage
		if (mergedDocWithoutPaymentInfo) {
			await documentStore.addPlanHolderEverplansMergedPdf(
				planHolder.id,
				mergedDocWithoutPaymentInfo
			)
		}
	}

	const redactDocument = async (
		document: StoreDocument
	): Promise<StoreDocument> => {
		const fieldsNames = getFieldNamesToRedact(document)
		document = structuredClone(toRaw(document))

		const pdfViewer = getPdfViewer()
		const foxitDoc = (await pdfViewer.loadPDFDocByFile(document.blob.slice(0), {
			fileName: document.meta.name,
		})) as PDFViewCtrl.PDF.PDFDoc

		const fieldsToRedact = await getFieldsToRedact(fieldsNames, foxitDoc)

		for (const field of fieldsToRedact) {
			redactField(field)
		}
		const fileBlob = await foxitDoc.getFile()
		document.blob = await fileBlob.arrayBuffer()

		return document
	}

	const getFieldNamesToRedact = (document: StoreDocument): Array<string> => {
		// TODO:  PSS-1816 - Make this function get the payment fields dynamically
		// Plan to implement with handling redaction for Everplans when E-signs required
		return [
			// EFT Initial Payment Fields
			'initialBankName',
			'initialBankCheckingCheck',
			'initialBankSavingsCheck',
			'initialBankRoutingNumber',
			'initialBankAccountNumber',
			// EFT Ongoing Payment Fields
			'bankInfoSameCheck',
			'ongoingBankMonthlyDrawDate',
			'ongoingBankName',
			'ongoingBankRoutingNumber',
			'ongoingBankAccountNumber',
			'ongoingBankCheckingCheck',
			'ongoingBankSavingsCheck',
			// Credit Card Payment Fields
			'creditCardNumber',
			'creditCardMonthlyDrawDate',
		]
	}

	const getFieldsToRedact = async (
		fieldNames: Array<string>,
		document: PDFViewCtrl.PDF.PDFDoc
	) => {
		const form = (await document?.loadPDFForm()) as unknown as FoxItPDFForm
		const fieldsToRedact = []
		for (const field of form.fields) {
			if (fieldNames.includes(field.info.name)) {
				fieldsToRedact.push(field)
			}
		}
		return fieldsToRedact
	}

	const getPlacementInfoFromField = (field: FoxItField) => {
		// TODO: PSS-1769: To be implemented during e-sign redaction ticket
	}

	const redactField = (field: FoxItField) => {
		field.setValue('****')
	}

	// Merges all the documents and opens the merged document
	const prepareMergedDocuments = async () => {
		// clear the current document to avoid saving merged doc to a step
		sessionDocumentsStore.clearCurrentDocument()

		// since documents are being re-merged, we clear previous missing signatures to retabulate them
		sessionDocumentsStore.clearMissingSignatures()

		for (const planHolder of sessionStore.activePlans || []) {
			if (planHolder.packetStatus === PlanPacketStatus.Incomplete) {
				// update return type if we want more details other than missing signatures
				const missingSignatures = await mergeDocsForPlanHolder(planHolder)

				await mergeEverplansDocsForPlanholder(planHolder)

				if (missingSignatures && missingSignatures.length > 0) {
					// update the status of merged documents (missing signatures etc.)
					sessionDocumentsStore.setMissingSignatures(
						planHolder.id,
						missingSignatures
					)
				}
			}
		}

		await sessionDocumentsStore.setPageState('complete')
	}

	const mergeDocsForPlanHolder = async (
		planHolder: Plan
	): Promise<MissingSignaturesBySigner[] | undefined> => {
		const pdfViewer = getPdfViewer()
		let mergedDoc: PDFViewCtrl.PDF.PDFDoc | null = null
		const planHolderDocuments = documentStore.getPlanHolderDocuments(planHolder)

		if (!planHolderDocuments) {
			return
		}

		for (const document of planHolderDocuments) {
			// Don't merge any document that's not completed
			if (!planHolder.completedSteps.includes(document.meta.type)) {
				continue
			}

			const doc = (await pdfViewer.loadPDFDocByFile(document.blob.slice(0), {
				fileName: document.meta.name,
			})) as PDFViewCtrl.PDF.PDFDoc

			if (!mergedDoc) {
				mergedDoc = doc
				continue
			}

			const modifiedBlob = await doc.getFile()

			await mergedDoc!.insertPages({
				destIndex: mergedDoc.getPageCount(),
				file: await modifiedBlob.arrayBuffer(),
				startIndex: 0,
				endIndex: doc.getPageCount() - 1,
			})
		}

		if (!mergedDoc) {
			return
		}

		// turn off render-file-success reactivity
		shouldNotRender.value = true

		// load merged doc into viewer
		const fileBlob = await mergedDoc!.getFile()
		const fileArrayBuffer = await fileBlob.arrayBuffer()
		const pdfui = getPdfui()
		await pdfui.openPDFByFile(fileArrayBuffer)
		const newDoc = pdfViewer.getCurrentPDFDoc()
		const newForm = (await newDoc?.loadPDFForm()) as unknown as FoxItPDFForm

		// collect all signature-related fields placements from the document
		const unsignedSignaturesAndDates =
			await saveUnsignedSignaturePlacementInfo(newForm)
		// separate the signatures and date as signature push buttons needs to be replaced
		const signaturesInfo = unsignedSignaturesAndDates.signatures
		const unsignedDateFields = unsignedSignaturesAndDates.dateFields

		const missingSignatures = []

		const unsignedFDSignatures = []
		// remove all buttons from the document
		for (const signatureField of signaturesInfo) {
			// check for funeral director signatures
			if (isFuneralDirectorSignature(signatureField.converted.name)) {
				unsignedFDSignatures.push(signatureField.converted)
			}
			await newForm.removeField(signatureField.converted.name)
		}

		const unsignedFdDateFields = unsignedDateFields.filter((field) =>
			isFuneralDirectorSignature(field.name)
		)

		if (unsignedFDSignatures.length) {
			missingSignatures.push({
				signatureFields: [...unsignedFDSignatures, ...unsignedFdDateFields],
				signer: 'FuneralDirector' as Signer,
			})
		}

		// flatten the document
		await newDoc!.flatten(0)

		// add the signatures back
		for (const signature of signaturesInfo) {
			const { pageIndex, rect, fieldName } = signature.signaturePlacement
			await newForm.addSignature(pageIndex, rect, fieldName)
		}

		// grab the updated document blob and save it to store
		const flattenedBlob = await newDoc!.getFile()
		const flattenedArrayBuffer = await flattenedBlob.arrayBuffer()
		await documentStore.addPlanHolderMergedDocuments(
			planHolder.id,
			flattenedArrayBuffer
		)

		// return render-file-success reactivity to normal
		shouldNotRender.value = false

		return missingSignatures
	}

	/**
	 * This method is used to merge documents for printing PDF preview on the home dashbaord
	 * @param documents
	 */
	const mergeDocs = async (
		documents: StoreDocument[]
	): Promise<ArrayBuffer | undefined> => {
		const pdfViewer = getPdfViewer()
		let mergedDoc: PDFViewCtrl.PDF.PDFDoc | null = null

		for (const document of documents) {
			const doc = (await pdfViewer.loadPDFDocByFile(document.blob.slice(0), {
				fileName: document.meta.name,
			})) as PDFViewCtrl.PDF.PDFDoc

			if (!mergedDoc) {
				mergedDoc = doc
				continue
			}

			const modifiedBlob = await doc.getFile()

			await mergedDoc!.insertPages({
				destIndex: mergedDoc.getPageCount(),
				file: await modifiedBlob.arrayBuffer(),
				startIndex: 0,
				endIndex: doc.getPageCount() - 1,
			})
		}

		if (!mergedDoc) {
			return
		}

		// turn off render-file-success reactivity
		shouldNotRender.value = true

		// load merged doc into viewer
		const fileBlob = await mergedDoc!.getFile()
		const fileArrayBuffer = await fileBlob.arrayBuffer()
		const pdfui = getPdfui()
		await pdfui.openPDFByFile(fileArrayBuffer)
		const newDoc = pdfViewer.getCurrentPDFDoc()
		const newForm = (await newDoc?.loadPDFForm()) as unknown as FoxItPDFForm

		// collect all signature placements from the document
		const signaturesInfo = (await saveUnsignedSignaturePlacementInfo(newForm))
			.signatures

		// remove all buttons from the document
		for (const signatureField of signaturesInfo) {
			await newForm.removeField(signatureField.converted.name)
		}

		// flatten the document
		await newDoc!.flatten(0)

		// add the signatures back
		for (const signature of signaturesInfo) {
			const { pageIndex, rect, fieldName } = signature.signaturePlacement
			await newForm.addSignature(pageIndex, rect, fieldName)
		}

		// grab the updated document blob and save it to store
		const flattenedBlob = await newDoc!.getFile()
		const flattenedArrayBuffer = await flattenedBlob.arrayBuffer()

		// return render-file-success reactivity to normal
		shouldNotRender.value = false

		return flattenedArrayBuffer
	}

	// Opens a PDF document and saves the current document
	const openPDF = async (planHolderID: string, storeDoc: StoreDocument) => {
		try {
			sessionDocumentsStore.setCurrentPlanHolderID(planHolderID)
			const blob = new Blob([storeDoc.blob], { type: 'application/pdf' }) // Convert ArrayBuffer back to Blob for PDF library
			const pdfui = getPdfui()

			await pdfui.openPDFByFile(blob as Blob)
			sessionDocumentsStore.setCurrentDocument(planHolderID, storeDoc.meta.type)
		} catch (error) {
			captureException(error, {
				extra: {
					message: 'Error opening PDF',
				},
			})
		}
	}

	// Registers events for the PDFViewer
	const registerEventEmitters = () => {
		const pdfViewer = getPdfViewer()
		if (debugEvents.value) {
			consoleEvents()
		}

		const stopPersistingSignatures = persistFoxitSignatures(
			pdfViewer,
			sessionStore.activeSession?.id
		)

		// This method prevents FoxIt's default behavior where the signature image floats in the page, it now places the signature directly on the field.
		pdfViewer.getEventEmitter().on('ink-image-selected', async (data) => {
			const viewer = getPdfViewer()
			const handlerManager = pdfViewer.getStateHandlerManager()
			const doc = viewer.getCurrentPDFDoc()
			const form = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm
			const field = form?.getField(
				activeSignature.value as string
			) as unknown as FoxItField
			const sigControl = field?.getControlByIndex(
				0
			) as PDFViewCtrl.PDF.form.PDFControl
			const widget = await sigControl.getWidgetAnnot()
			const pdfPage = widget.getPage()
			const rect = widget.getRect()
			const annot = (await pdfPage.addAnnot({
				rect,
				type: FoxItScreen,
				name: field.info.name,
			})) as unknown as FoxItAnnotScreen[]
			const resp = await fetch(data)
			const imageBuffer = await resp.arrayBuffer()

			handlerManager.switchTo(FoxItHandState, undefined)

			await annot[0].setImage(imageBuffer)
			await form?.removeField(field?.info.name as string)

			const currentDocumentType =
				sessionDocumentsStore.currentDocument?.meta.type

			setSignatureDateField(
				form!,
				activeSignature.value as string,
				new Date().toLocaleDateString(),
				currentDocumentType
			)

			setSignatureRelatedTextField(
				form!,
				getNormalizedSignatureFieldName(activeSignature.value as string)!,
				'Time',
				DateTime.now().toLocaleString(DateTime.TIME_SIMPLE)
			)

			clearActiveSignature()
		})

		// When the PDF is rendered, go to the first page.
		pdfViewer.getEventEmitter().on('render-file-success', async (data) => {
			const viewer = getPdfViewer()

			if (shouldNotRender.value) {
				return
			}

			// Viewer has the new file now; bind identity before autofill/signature setup.
			// Covers the case where openPDFByFile's promise resolves after this handler reaches setFieldValues.
			const { status, meta } = sessionDocumentsStore.pageState
			if (
				!sessionDocumentsStore.currentDocument &&
				status === 'switch' &&
				meta?.planHolderID &&
				meta?.document
			) {
				sessionDocumentsStore.setCurrentDocument(
					meta.planHolderID,
					meta.document.meta.type
				)
			}

			await viewer.getPDFDocRender()?.goToPage(0)
			const doc = viewer.getCurrentPDFDoc()
			const pdfForm = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm

			if (sessionDocumentsStore.currentStep?.completed === false) {
				setAdditionalRequiredFields(pdfForm)

				viewer.setFormHighlightColor(0x00ffffff, 0xccf6efc2)
				await revertAllSignedFieldsToButtons()
				await turnAllSignaturesToPushButtons(pdfForm)
				await appendUuidToSignatureDateFields(pdfForm)
			} else {
				viewer.setFormHighlightColor(0x00ffffff, 0x00ffffff)
			}

			if (
				sessionDocumentsStore.currentStep!.completed === false &&
				sessionDocumentsStore.currentStep?.autofill === false
			) {
				await setFieldValues()
			}

			await viewer.redraw()
			await sessionDocumentsStore.setPageState('ready', {
				planHolderID: sessionDocumentsStore.currentPlanHolderID,
			})
		})

		// When a push button is tapped, it will show the signature dropdown or dialog.
		pdfViewer.getEventEmitter().on('tap-field', async (data) => {
			if (data.info.fieldType === 1) {
				const viewer = getPdfViewer()
				await viewer.killFocus()
				activeSignature.value = data.info.field

				if (viewer.getInkSignList('base64').length > 0) {
					await showSignatureDropdown()
				} else {
					await showSignatureDialog()
				}
			}
		})

		// In case the user placed the wrong signature, this method reverts a signature back to a push button.
		pdfViewer
			.getEventEmitter()
			.on('tap-annotation', async (data: { annot: FoxItAnnot }) => {
				const viewer = getPdfViewer()
				await viewer.killFocus()
				await revertSignedAnnotationToButton(data, pdfViewer)
			})

		const pdfui = getPdfui()
		pdfui.getComponentByName('create-signature-btn').then((component) => {
			component.on('click', async () => {
				addCursiveFontToSignatureDialog()
			})
		})

		return stopPersistingSignatures
	}

	// Logs all events triggered in the PDFViewer if debugEvents is true.
	const consoleEvents = () => {
		const pdfViewer = getPdfViewer()
		const emitter = pdfViewer.getEventEmitter()
		const originalEmit = emitter.emit

		emitter.emit = (eventName, ...args) => {
			console.log('Event triggered:', eventName, args) // tester function for debugging

			return originalEmit.apply(emitter, [eventName, ...args])
		}
	}

	const editDocument = async () => {
		const pdfViewer = getPdfViewer()

		if (!pdfViewer) {
			return
		}

		pdfViewer.setFormHighlightColor(0x00ffffff, 0xccf6efc2)
		await revertAllSignedFieldsToButtons()
		await setFilledFieldsToTransparent()
	}

	// Signed fields are stored as annotations until the document is returned to
	// edit mode. Editing removes those annotations, so callers must be able to
	// warn the user before continuing.
	const hasSignedAnnotations = async () => {
		const pdfViewer = getPdfViewer()
		const viewerDoc = pdfViewer?.getCurrentPDFDoc()

		if (!viewerDoc) {
			return false
		}

		for (let index = 0; index < viewerDoc.getPageCount(); index++) {
			const page = await viewerDoc.getPageByIndex(index)
			const annotations = (await page.getAnnots()) as FoxItAnnot[]

			if (
				annotations.some(
					(annotation) =>
						annotation.info.name.includes('Signature') ||
						annotation.info.name.includes('Initial')
				)
			) {
				return true
			}
		}

		return false
	}

	const confirmResetForm = async () => {
		await sessionDocumentsStore.setPageState('pending')
		await documentStore.useNewTemplateForPlanHolderDocument(
			sessionDocumentsStore.currentPlanHolderID,
			sessionDocumentsStore.currentDocument!
		)
		await sessionDocumentsStore.setStepCompleted(
			sessionDocumentsStore.currentStep!,
			false
		)
		sessionDocumentsStore.setStepAutoFilled(
			sessionDocumentsStore.currentStep!,
			false
		)

		await openPDF(
			sessionDocumentsStore.currentPlanHolderID,
			sessionDocumentsStore.currentDocument!
		)
	}

	const confirmPayment = async () => {
		const pdfViewer = getPdfViewer()
		const doc = pdfViewer.getCurrentPDFDoc()
		const pdfForm = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm

		setAdditionalRequiredFields(pdfForm)
		await setFieldValues()
		await revertAllSignedFieldsToButtons()
		pdfViewer.redraw()
	}

	return {
		confirmModalOpen,
		pageStateListener,
		shouldNotRender,
		saveCurrentDocument,
		openPDF,
		registerEventEmitters,
		editDocument,
		hasSignedAnnotations,
		confirmResetForm,
		confirmPayment,
		mergeDocs,
		mergeDocsForPlanHolder,
	}
}
